<template>
  <div class="resumeRenderer" :class="resumeTemplate">
    <CoverPage
      ref="coverItem"
      :resume="resume"
      :current-category-id="currentCategoryId"
      :current-item-id="currentItemId"
      :items="coverItems"
      :titles="titles"
      :is-printing="isPrinting"
      :select-category="selectCategory"
      :select-item="selectItem"
      @nb-of-pages="nbOfPages"
    />
    <div ref="pages" class="pages">
      <div v-for="(page, i) in pages" :key="`page-${i}`" :ref="`page-${i}`" class="page" :class="{ print: isPrinting }">
        <div v-if="needSpacer(i, 1)" class="spacer first"></div>
        <template v-for="j in page.nbItem" :key="`page-${i}-item-${j}-item`">
          <div v-if="needSpacer(i, j)" class="spacer"></div>
          <div
            v-if="getItem(i, j).type !== getPreviousItem(i, j).type && !getItem(i, j).parentId"
            :class="`part-title type-${getItem(i, j).type}`"
          >
            <div class="title">
              {{ getCategorieTitle(getItem(i, j).type) }}
            </div>
          </div>
          <component
            :is="itemRendererComponent(getItem(i, j).type)"
            :ref="`${getItem(i, j).id}`"
            :is-selected="currentItemId === getItem(i, j).id"
            :item="getItem(i, j)"
            :content="getItem(i, j).content"
            :current-section="currentSection"
            class="item"
            :class="{ 'new-page': getItem(i, j)?.content?.startNewPage }"
            :on-clicked="section => selectItem(getItem(i, j).id, section)"
            :resume-language="resume.language || 'fr'"
          />
        </template>
        <div class="spacer end"></div>
        <div class="footer">
          <div class="page-number">{{ i + 1 + nbOfCoverPage }}</div>
          <div v-if="resume.contact" class="text">
            {{ resume.contact.companyName }} - {{ resume.contact.phoneNumber }} -
            {{ resume.contact.email }}
          </div>
        </div>
      </div>
    </div>
    <EndPage class="endpage page" :resume="resume" :class="{ print: isPrinting }" />
  </div>
</template>

<script>
import CoverPage from '@/components/CoverPage';
import EndPage from '@/components/EndPage';
import ExperienceItem from '@/components/items/Experience';
import ExperienceProjectItem from '@/components/items/ExperienceProject';
import FormationItem from '@/components/items/Formation';
import ProjectItem from '@/components/items/Project';
import SkillsItem from '@/components/panels/Skills/Skill.vue';
import ExperienceContext from '@/components/items/ExperienceContext.vue';
import ExperienceMissions from '@/components/items/ExperienceMissions.vue';
import ExperienceTechnicalEnvironment from '@/components/items/ExperienceTechnicalEnvironment.vue';
import { toRaw } from 'vue';
import debounce from 'lodash/debounce';

const categoriesOrder = ['formations', 'projects', 'experiences'];

const defaultTemplateSettings = {
  sectionPosition: [
    { sectionId: 'experiences', pageType: 'page' },
    { sectionId: 'experiences-projects', pageType: 'page' },
    { sectionId: 'formations', pageType: 'page' },
    { sectionId: 'projects', pageType: 'page' },
    { sectionId: 'skills', pageType: 'cover' },
    { sectionId: 'conviction', pageType: 'cover' },
  ],
};

export default {
  name: 'ResumeRenderer',
  components: {
    CoverPage,
    EndPage,
    ExperienceItem,
    ExperienceProjectItem,
    FormationItem,
    ProjectItem,
    SkillsItem,
    ExperienceContext,
    ExperienceMissions,
    ExperienceTechnicalEnvironment,
  },
  props: {
    currentItemId: {
      type: String,
      default: '',
    },
    resume: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    isPrinting: {
      type: Boolean,
      default: false,
    },
    currentSection: {
      type: String,
      default: () => '',
    },
    currentCategoryId: {
      type: String,
      default: () => '',
    },
  },

  emits: ['itemSelected', 'categorySelected', 'pages'],

  data: function () {
    return {
      resumeTemplate: '',
      pages: [],
      categories: [
        {
          id: 'experiences',
          rendererComponent: 'ExperienceItem',
          pageType: 'page',
        },
        {
          id: 'experiences-context',
          rendererComponent: 'ExperienceContext',
          pageType: 'page',
        },
        {
          id: 'experiences-missions',
          rendererComponent: 'ExperienceMissions',
          pageType: 'page',
        },
        {
          id: 'experiences-technical-environment',
          rendererComponent: 'ExperienceTechnicalEnvironment',
          pageType: 'page',
        },
        {
          id: 'experiences-projects',
          rendererComponent: 'ExperienceProjectItem',
          pageType: 'page',
          subItem: true,
        },
        {
          id: 'experiences-projects-contexts',
          rendererComponent: 'ExperienceProjectContextItem',
        },
        {
          id: 'formations',
          rendererComponent: 'FormationItem',
          pageType: 'page',
        },
        { id: 'projects', rendererComponent: 'ProjectItem', pageType: 'page' },
        { id: 'skills', rendererComponent: 'SkillsItem', pageType: 'cover' },
        { id: 'conviction', rendererComponent: null, pageType: 'cover' },
      ],
      sortedItems: [],
      coverItems: [],
      processing: false,
      currentTemplateId: null,
      nbOfCoverPage: 1,
    };
  },

  computed: {
    titles() {
      const titles = this.items.find(i => i.type === 'titles');
      return titles ? titles.content : undefined;
    },
  },

  watch: {
    currentItemId() {
      if (!this.currentItemId) return;
      this.scrollTo(this.currentItemId);
    },
    items: {
      handler: debounce(function () {
        this.updateItems();
        this.$nextTick(this.recalculatePage);
      }, 500),
      deep: true,
    },
    resume: {
      handler: function (resume) {
        if (resume.template) {
          this.updateSettings(defaultTemplateSettings);

          if (this.resume.template.settings) {
            this.updateSettings(this.resume.template.settings);
          }
          this.resumeTemplate = resume.template.name || this.resumeTemplate;
          if (this.currentTemplateId !== resume.template.id) {
            this.currentTemplateId = resume.template.id;
            this.recalculateAll();
          }
          this.$nextTick(() => this.updateTemplateColor(resume.template));
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.updateSettings(defaultTemplateSettings);
    if (this.resume.template) {
      this.$addTemplateCss(this.resume.template);
      if (this.resume.template.settings) {
        this.updateSettings(this.resume.template.settings);
      }
      this.resumeTemplate = this.resume.template.name;
      this.$nextTick(() => this.updateTemplateColor(this.resume.template));
    }
    this.updateItems();
    setTimeout(this.recalculatePage, 1000);
  },

  methods: {
    updateSettings(settings) {
      if (settings.sectionPosition) {
        settings.sectionPosition.forEach(i => {
          const categorie = this.categories.find(c => c.id === i.sectionId);
          if (categorie) {
            categorie.pageType = i.pageType;
          }
        });
      }
      if (settings.sections) {
        const disabledSection = settings.sections.filter(s => s.disabled);
        disabledSection.forEach(s => {
          const categorie = this.categories.find(c => c.id === s.id);
          if (categorie) {
            categorie.pageType = null;
          }
        });
      }
    },
    updateTemplateColor(template) {
      if (document.getElementsByClassName(template.name)[0]) {
        template.variables.forEach(v => {
          document.getElementsByClassName(template.name)[0].style.setProperty(`--${v.name}`, v.value || null);
        });
      }
    },
    needSpacer(pageIndex, itemIndex) {
      const categoriesWithSpacer = ['experiences', 'projects', 'formations'];
      const currentCategorieId = this.getItem(pageIndex, itemIndex).type;
      const previousCategorieId = this.getPreviousItem(pageIndex, itemIndex).type;

      if (!previousCategorieId) {
        return false;
      }
      if (itemIndex === 1 && currentCategorieId === previousCategorieId) {
        return true;
      }
      return !!(
        itemIndex !== 1 &&
        categoriesWithSpacer.includes(currentCategorieId) &&
        currentCategorieId === previousCategorieId
      );
    },
    selectItem(id, section) {
      this.$emit('itemSelected', id, section);
    },
    nbOfPages(nbOfPages) {
      this.nbOfCoverPage = nbOfPages;
    },
    selectCategory(id) {
      this.$emit('categorySelected', id);
    },
    getPreviousItem(page, index) {
      let absoluteIndex = index - 1;
      for (let i = 0; i < page; i++) {
        absoluteIndex += this.pages[i].nbItem;
      }
      if (absoluteIndex === 0) {
        return {};
      }
      return this.sortedItems[absoluteIndex - 1] || {};
    },
    getItem(page, index) {
      let absoluteIndex = index - 1;
      for (let i = 0; i < page; i++) {
        absoluteIndex += this.pages[i].nbItem;
      }
      return this.sortedItems[absoluteIndex] || {};
    },
    getPage(itemId) {
      for (let i = 0; i < this.sortedItems.length; i++) {
        if (this.sortedItems[i].id === itemId) {
          let nbItems = 0;
          for (let j = 0; j < this.pages.length; j++) {
            nbItems += this.pages[j].nbItem;
            if (i < nbItems) {
              return j;
            }
          }
        }
      }
    },
    addEmptyPage() {
      this.pages.push({ nbItem: 0 });
    },
    async recalculatePage() {
      if (this.processing) return;
      this.processing = true;

      if (this.pages.length === 0 && this.sortedItems.length !== 0) {
        this.addEmptyPage();
        this.pages[0].nbItem = this.sortedItems.length;
        this.$nextTick(this.recalculatePage);
        this.processing = false;
        return;
      }

      this.pages = this.pages.map(_ => ({ nbItem: 0 }));
      if (this.pages.length === 0) {
        this.addEmptyPage();
      }

      this.pages[0].nbItem = this.sortedItems.length;
      await this.$nextTick();

      const domItems = Array.from(this.$refs[`page-0`][0].children);
      const footerItem = domItems.pop();
      const { marginTop, marginBottom } = getComputedStyle(footerItem);
      const footerHeight = footerItem.getBoundingClientRect().height + parseInt(marginTop) + parseInt(marginBottom);

      for (let i = 0; i < this.pages.length; i++) {
        if (!this.$refs[`page-${i}`] || !this.$refs[`page-${i}`][0]) {
          this.processing = false;
          return;
        }

        let itemInPageCount = 0;
        for (const element of Array.from(this.$refs[`page-${i}`][0].children)) {
          const pageFirstElem = Array.from(this.$refs[`page-${i}`][0].children).filter(elem =>
            elem.classList.contains('item')
          )[0];

          if (element.classList.contains('item')) {
            if (element.classList.contains('new-page') && pageFirstElem !== element) {
              break;
            }

            const parentElementBottom = element.parentElement.getBoundingClientRect().bottom - footerHeight;

            const elementMarginBottom = parseInt(getComputedStyle(element).marginBottom) || 0;
            const elementBottom = element.getBoundingClientRect().bottom + elementMarginBottom;

            if (elementBottom > parentElementBottom) continue;

            itemInPageCount++;
          }
        }
        const itemCountForNextPage = this.pages[i].nbItem - itemInPageCount;
        if (itemCountForNextPage > 0) {
          this.addEmptyPage();
          this.pages[i + 1].nbItem = itemCountForNextPage;
        }
        this.pages[i].nbItem = itemInPageCount;

        await this.$nextTick();
      }

      this.pages = this.pages.filter(page => page.nbItem > 0);
      await this.$nextTick();
      this.$emit('pages', this.pages);

      this.scrollTo(this.currentItemId, 'instant');

      this.processing = false;

      if (this.resume.template.settings && this.resume.template.settings.execute) {
        eval(this.resume.template.settings.execute);
      }
    },
    itemRendererComponent(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      if (category && category.rendererComponent) {
        return category.rendererComponent;
      }
      console.error(`Missing renderer component for category ${categoryId}`);
      return null;
    },
    getCategorieTitle(categorieId) {
      if (this.titles && this.titles[categorieId]) {
        return this.titles[categorieId];
      }

      return this.$t(`resume.sections.${this.resume.language || 'fr'}.${categorieId}`);
    },
    updateItems() {
      const items = JSON.parse(JSON.stringify(this.items));
      this.coverItems = items.filter(i => {
        const category = this.categories.find(c => c.id === i.type);
        if (!category) return false;
        return category.pageType === 'cover';
      });
      this.sortedItems = items.filter(i => {
        const category = this.categories.find(c => c.id === i.type);
        if (!category) return false;
        return category.pageType === 'page';
      });
      this.sortedItems.forEach(i => {
        i.position = categoriesOrder.indexOf(i.type) * 100 + i.order;
        if (i.parentId) {
          // sort with parentId
          const parent = this.items.find(item => item.id === i.parentId);
          i.position = categoriesOrder.indexOf(parent.type) * 100 + parent.order + 10 - i.order;
        }
      });
      this.sortedItems.sort((a, b) => {
        return a.position - b.position;
      });

      this.sortedItems = this.sortedItems.reduce((acc, item) => {
        if (item.type !== 'experiences') acc.push(item);
        else {
          const clonedItem = structuredClone(toRaw(item));
          clonedItem.type = 'experiences-context';
          clonedItem.parentId = !!acc.find(item => item.type === 'experiences-context') ? 1 : null;
          acc.push(clonedItem);

          if (item.content.missions) {
            const clonedItem = structuredClone(toRaw(item));
            clonedItem.type = 'experiences-missions';
            clonedItem.content.startNewPage = false;
            clonedItem.parentId = item.id;
            acc.push(clonedItem);
          }

          if (item.content.technos) {
            const clonedItem = structuredClone(toRaw(item));
            clonedItem.type = 'experiences-technical-environment';
            clonedItem.content.startNewPage = false;
            clonedItem.parentId = item.id;
            acc.push(clonedItem);
          }
        }

        return acc;
      }, []);
    },
    scrollTo(id, behavior = 'smooth') {
      if (!this.$refs[id] || !this.$refs[id][0]) {
        document.querySelector(`#resumeRenderer`).scrollTo({ left: 0, top: 0, behavior });
      } else {
        let scale = 1;
        if (window.innerWidth < 1000) {
          scale = 0.4;
        } else if (window.innerWidth < 1370) {
          scale = 0.6;
        }
        const pageIndex = this.getPage(id);
        let calcul = (this.$refs[`page-${pageIndex}`][0].offsetTop + this.$refs[id][0].$el.offsetTop - 64) * scale;
        document.querySelector(`#resumeRenderer`).scrollTo({ left: 0, top: calcul, behavior });
      }
    },
    recalculateAll() {
      this.pages = [];
      this.sortedItems = [];
      this.updateItems();
      this.$nextTick(this.recalculatePage);
    },
  },
};
</script>

<style scoped lang="scss">
.resumeRenderer {
  width: fit-content;
  .page,
  .coverpage {
    -webkit-print-color-adjust: exact;
    width: 21cm;
    height: 29.69cm;
    background-color: white;
    margin: 24px auto;
    box-shadow:
      0 2px 8px -2px rgba(0, 0, 0, 0.1),
      0 4px 12px -4px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    position: relative;
    &.print {
      margin: 0;
      border-radius: 0;
      box-shadow: none;
    }
    .item,
    .part-title {
      flex-shrink: 0;
    }
    .spacer {
      &:before {
        content: '';
        display: block;
        width: 1px;
        height: 0.5cm;
        margin-left: calc(4cm - 0.5px);
      }
      &.end {
        flex-grow: 1;
        &:before {
          height: 100%;
        }
      }
    }
  }
}
</style>
